import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { z } from "zod";
import type { Member } from "@chat-yai/shared";
import { superApi } from "@/api/endpoints/super";
import { Modal } from "@/components/Modal";
import { useToast } from "@/contexts/ToastContext";
import { ApiError } from "@/api/client";
import { translateError } from "@/i18n/errors";
import { authStorage } from "@/lib/auth-storage";
import { FormError } from "@/components/FormError";

const impersonationSchema = z.object({
  reason: z.string().min(3, "Provide a reason (3+ characters)."),
  ticket_ref: z.string().optional(),
});
type ImpersonationValues = z.infer<typeof impersonationSchema>;

export function SuperTenantDetailPage(): JSX.Element {
  const params = useParams({ strict: false }) as { id?: string };
  const tenantId = params.id ?? "";
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [planOpen, setPlanOpen] = useState(false);
  const [planChoice, setPlanChoice] = useState<string>("");
  const [impTarget, setImpTarget] = useState<Member | null>(null);
  const impForm = useForm<ImpersonationValues>();

  const detailQuery = useQuery({
    queryKey: ["super", "tenants", tenantId],
    queryFn: () => superApi.getTenant(tenantId),
    enabled: !!tenantId,
  });
  const plansQuery = useQuery({
    queryKey: ["super", "plans"],
    queryFn: () => superApi.listPlans(),
  });

  const planMut = useMutation({
    mutationFn: (plan_code: string) => superApi.changePlan(tenantId, { plan_code }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["super", "tenants", tenantId] });
      setPlanOpen(false);
      toast.push("success", "Plan updated.");
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  const statusMut = useMutation({
    mutationFn: (status: "active" | "suspended") =>
      superApi.changeStatus(tenantId, { status }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["super", "tenants", tenantId] });
      toast.push("success", "Status updated.");
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  const impersonateMut = useMutation({
    mutationFn: (v: ImpersonationValues) => {
      if (!impTarget) throw new Error("no-target");
      return superApi.startImpersonation({
        target_user_id: impTarget.user_id,
        target_tenant_id: tenantId,
        reason: v.reason,
        ticket_ref: v.ticket_ref || undefined,
      });
    },
    onSuccess: async (res) => {
      authStorage.set(res.access_token);
      await queryClient.invalidateQueries();
      setImpTarget(null);
      navigate({ to: "/dashboard" });
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  if (!detailQuery.data) {
    return (
      <div className="page">
        <div className="empty">Loading workspace…</div>
      </div>
    );
  }

  const detail = detailQuery.data;

  return (
    <div className="page" data-testid="super-tenant-detail">
      <div className="page-head">
        <div>
          <div className="page-title">{detail.tenant.name}</div>
          <div className="page-sub mono">
            {detail.tenant.slug} · created {new Date(detail.tenant.created_at).toLocaleDateString()}
          </div>
        </div>
        <div className="page-actions">
          {detail.tenant.status === "active" ? (
            <button
              className="btn btn-outline btn-sm"
              onClick={() => statusMut.mutate("suspended")}
              data-testid="suspend-tenant"
            >
              Suspend
            </button>
          ) : (
            <button
              className="btn btn-outline btn-sm"
              onClick={() => statusMut.mutate("active")}
              data-testid="reactivate-tenant"
            >
              Reactivate
            </button>
          )}
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              setPlanChoice(detail.plan.code);
              setPlanOpen(true);
            }}
            data-testid="open-change-plan"
          >
            Change plan
          </button>
        </div>
      </div>

      <div className="kpi-grid" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
        <div className="kpi">
          <div className="kpi-label">Plan</div>
          <div className="kpi-value" style={{ fontSize: 22 }} data-testid="current-plan">
            {detail.plan.name}
          </div>
          <div className="kpi-meta mono">{detail.plan.code}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Status</div>
          <div className="kpi-value" style={{ fontSize: 22, textTransform: "capitalize" }}>
            {detail.tenant.status}
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Members</div>
          <div className="kpi-value" style={{ fontSize: 22 }}>
            {detail.members.length}
          </div>
        </div>
      </div>

      <div className="col-2" style={{ marginTop: "var(--s-6)" }}>
        <div className="card">
          <div className="sect-head">
            <h3>Members</h3>
          </div>
          <div
            className="table-row-head"
            style={{ gridTemplateColumns: "2fr 1fr 120px" }}
          >
            <div>Member</div>
            <div>Role</div>
            <div></div>
          </div>
          {detail.members.map((m) => (
            <div
              key={m.user_id}
              className="list-row"
              style={{ gridTemplateColumns: "2fr 1fr 120px" }}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{m.display_name ?? m.email}</div>
                <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
                  {m.email}
                </div>
              </div>
              <div>
                <span className="chip" style={{ textTransform: "capitalize" }}>
                  {m.role}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => {
                    setImpTarget(m);
                    impForm.reset();
                  }}
                  data-testid={`open-impersonate-${m.email}`}
                >
                  Impersonate
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="card">
          <div className="sect-head">
            <h3>Recent activity</h3>
          </div>
          {detail.recent_audit_summary.length ? (
            <div>
              {detail.recent_audit_summary.map((a) => (
                <div
                  key={a.id}
                  className="list-row"
                  style={{ gridTemplateColumns: "1fr" }}
                >
                  <div>
                    <div className="audit-action">{a.action}</div>
                    <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
                      {a.actor_email ?? "system"} · {new Date(a.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty">No recent activity.</div>
          )}
        </div>
      </div>

      <Modal
        title="Change plan"
        open={planOpen}
        onClose={() => setPlanOpen(false)}
        footer={
          <>
            <button className="btn btn-ghost btn-sm" onClick={() => setPlanOpen(false)}>
              Cancel
            </button>
            <button
              className="btn btn-primary btn-sm"
              disabled={planMut.isPending || !planChoice || planChoice === detail.plan.code}
              onClick={() => planMut.mutate(planChoice)}
              data-testid="confirm-change-plan"
            >
              {planMut.isPending ? "Updating…" : "Confirm change"}
            </button>
          </>
        }
      >
        <p className="help">Changing the plan adjusts limits immediately. The tenant is notified by email.</p>
        <div className="field-group">
          <label>Plan</label>
          <select
            className="field-input"
            value={planChoice}
            onChange={(e) => setPlanChoice(e.target.value)}
            data-testid="plan-select"
          >
            {plansQuery.data?.plans.map((p) => (
              <option key={p.code} value={p.code}>
                {p.name} (NZ${(p.price_cents / 100).toFixed(0)} {p.billing_cycle})
              </option>
            ))}
          </select>
        </div>
      </Modal>

      <Modal
        title={`Impersonate · ${impTarget?.email ?? ""}`}
        open={!!impTarget}
        onClose={() => setImpTarget(null)}
        footer={
          <>
            <button className="btn btn-ghost btn-sm" onClick={() => setImpTarget(null)}>
              Cancel
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={impForm.handleSubmit((v) => impersonateMut.mutate(v))}
              disabled={impersonateMut.isPending}
              data-testid="confirm-impersonate"
            >
              {impersonateMut.isPending ? "Starting…" : "Start impersonation"}
            </button>
          </>
        }
      >
        <p className="help">
          Every action you take while impersonating is recorded in the audit log with your real
          identity attached.
        </p>
        <div className="field-group">
          <label>Reason (required)</label>
          <textarea
            className="field-input"
            rows={3}
            data-testid="impersonate-reason"
            {...impForm.register("reason", {
              required: "Reason is required.",
              minLength: { value: 3, message: "At least 3 characters." },
            })}
          />
          <FormError message={impForm.formState.errors.reason?.message} />
        </div>
        <div className="field-group">
          <label>Support ticket reference (optional)</label>
          <input
            className="field-input mono"
            placeholder="ZD-12345"
            data-testid="impersonate-ticket"
            {...impForm.register("ticket_ref")}
          />
        </div>
      </Modal>
    </div>
  );
}
